import {
  getAccountNumberError,
  getPhoneNumberError,
  normalizePhoneNumber,
  validateRoutingNumber,
} from '@steerfi/shared';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import type { AddressVerificationResponse, StandardizedAddress } from '@/api/address';
import {
  type AddressFormData,
  AddressFormFields,
  AddressVerificationDialog,
  type AddressVerificationStatus,
} from '@/components/address';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface IndividualRegistrationFormProps {
  onBack: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  // Address info
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  addressVerificationStatus: AddressVerificationStatus;
  // ACH info
  achAccountNumber: string;
  achRoutingNumber: string;
  achAccountType: 'checking' | 'savings';
  achBankName: string;
  achAccountHolderName: string;
}

interface FormErrors {
  [key: string]: string;
}

export function IndividualRegistrationForm({ onBack }: IndividualRegistrationFormProps) {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    addressVerificationStatus: 'not_checked',
    achAccountNumber: '',
    achRoutingNumber: '',
    achAccountType: 'checking',
    achBankName: '',
    achAccountHolderName: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Personal Info + Address, 2: ACH Info

  // Address verification state
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [addressVerificationResult, setAddressVerificationResult] =
    useState<AddressVerificationResponse | null>(null);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle address field changes
  const handleAddressChange = (field: keyof AddressFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Reset verification status when address changes
    if (formData.addressVerificationStatus !== 'not_checked') {
      setFormData((prev) => ({ ...prev, addressVerificationStatus: 'not_checked' }));
    }
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Handle address verification result
  const handleAddressVerificationResult = (result: AddressVerificationResponse) => {
    setAddressVerificationResult(result);
    // Show dialog if there are corrections or if verification failed
    if (result.hasCorrections || !result.verified) {
      setShowAddressDialog(true);
    }
  };

  // Handle accepting suggested address
  const handleAcceptSuggestedAddress = (suggested: StandardizedAddress) => {
    setFormData((prev) => ({
      ...prev,
      address1: suggested.streetAddress,
      address2: suggested.secondaryAddress || '',
      city: suggested.city,
      state: suggested.state,
      zipCode: suggested.zipCode,
      addressVerificationStatus: 'verified',
    }));
    setShowAddressDialog(false);
  };

  // Handle keeping original address
  const handleKeepOriginalAddress = () => {
    setFormData((prev) => ({
      ...prev,
      addressVerificationStatus: addressVerificationResult?.verified ? 'verified' : 'not_verified',
    }));
    setShowAddressDialog(false);
  };

  const validateStep1 = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    const phoneError = getPhoneNumberError(formData.phoneNumber);
    if (phoneError) {
      newErrors.phoneNumber = phoneError;
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Address validation
    if (!formData.address1.trim()) {
      newErrors.address1 = 'Street address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state) {
      newErrors.state = 'State is required';
    }
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
      newErrors.zipCode = 'ZIP code must be 5 digits or 5+4 format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.achAccountHolderName.trim()) {
      newErrors.achAccountHolderName = 'Account holder name is required';
    }

    // Use improved routing number validation with detailed error messages
    const routingValidation = validateRoutingNumber(formData.achRoutingNumber);
    if (!routingValidation.isValid && routingValidation.message) {
      newErrors.achRoutingNumber = routingValidation.message;
    }

    // Use improved account number validation with detailed error messages
    const accountError = getAccountNumberError(formData.achAccountNumber);
    if (accountError) {
      newErrors.achAccountNumber = accountError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep2()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await fetch('/api/auth/register/individual', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: normalizePhoneNumber(formData.phoneNumber),
          password: formData.password,
          // Address fields
          address1: formData.address1.trim(),
          address2: formData.address2.trim() || null,
          city: formData.city.trim(),
          state: formData.state,
          zipCode: formData.zipCode,
          addressVerificationStatus: formData.addressVerificationStatus,
          // ACH fields
          achAccountNumber: formData.achAccountNumber,
          achRoutingNumber: formData.achRoutingNumber,
          achAccountType: formData.achAccountType,
          achBankName: formData.achBankName || null,
          achAccountHolderName: formData.achAccountHolderName,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Redirect to registration success page
        window.location.href = '/registration-success?registered=true';
      } else {
        // Handle 409 Conflict - email already exists
        if (response.status === 409) {
          const message = data.message?.toLowerCase() || '';
          if (message.includes('email') || message.includes('account')) {
            setErrors({
              email: 'An account with this email already exists. Please log in instead.',
            });
            // Navigate back to step 1 where the email field is
            setCurrentStep(1);
            return;
          }
        }

        // Handle specific error cases
        if (response.status === 501 || data.message?.includes('not yet implemented')) {
          setErrors({
            general:
              'Individual registration is currently not available. Please use business registration or contact support for assistance.',
          });
        } else {
          setErrors({ general: data.message || 'Registration failed' });
        }
      }
    } catch (error) {
      setErrors({
        general: `An error occurred during registration. Please try again.  ${error instanceof Error ? error.message : ''}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={currentStep === 1 ? onBack : () => setCurrentStep(1)}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Individual Registration</CardTitle>
            <CardDescription>
              {currentStep === 1
                ? 'Step 1 of 2: Personal Information and Address'
                : 'Step 2 of 2: ACH Account Information'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-4 animate-fade-slide-in">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">
                        First Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="firstName"
                        placeholder="John"
                        value={formData.firstName}
                        onChange={(e) => handleChange('firstName', e.target.value)}
                        className={errors.firstName ? 'border-destructive' : ''}
                      />
                      {errors.firstName && (
                        <p className="text-sm text-destructive">{errors.firstName}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lastName">
                        Last Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="lastName"
                        placeholder="Doe"
                        value={formData.lastName}
                        onChange={(e) => handleChange('lastName', e.target.value)}
                        className={errors.lastName ? 'border-destructive' : ''}
                      />
                      {errors.lastName && (
                        <p className="text-sm text-destructive">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">
                      Email <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john.doe@example.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={errors.email ? 'border-destructive' : ''}
                    />
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber">
                      Phone Number <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="phoneNumber"
                      type="tel"
                      placeholder="+1 234 567 8900"
                      value={formData.phoneNumber}
                      onChange={(e) => handleChange('phoneNumber', e.target.value)}
                      className={errors.phoneNumber ? 'border-destructive' : ''}
                    />
                    {errors.phoneNumber && (
                      <p className="text-sm text-destructive">{errors.phoneNumber}</p>
                    )}
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="password">
                        Password <span className="text-destructive">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={(e) => handleChange('password', e.target.value)}
                          className={errors.password ? 'border-destructive pr-10' : 'pr-10'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="text-sm text-destructive">{errors.password}</p>
                      )}
                      <p className="text-xs text-muted-foreground">At least 8 characters</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">
                        Confirm Password <span className="text-destructive">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={formData.confirmPassword}
                          onChange={(e) => handleChange('confirmPassword', e.target.value)}
                          className={errors.confirmPassword ? 'border-destructive pr-10' : 'pr-10'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <p className="text-sm text-destructive">{errors.confirmPassword}</p>
                      )}
                    </div>
                  </div>

                  {/* Address Section */}
                  <div className="pt-4 border-t">
                    <h3 className="text-lg font-medium mb-4">Your Address</h3>
                    <AddressFormFields
                      address={{
                        address1: formData.address1,
                        address2: formData.address2,
                        city: formData.city,
                        state: formData.state,
                        zipCode: formData.zipCode,
                      }}
                      errors={errors}
                      onChange={handleAddressChange}
                      onVerificationResult={handleAddressVerificationResult}
                      onVerificationStatusChange={(status) =>
                        setFormData((prev) => ({ ...prev, addressVerificationStatus: status }))
                      }
                    />
                  </div>

                  <Button type="button" className="w-full" size="lg" onClick={handleNext}>
                    Next: ACH Information
                  </Button>
                </div>
              )}

              {/* Step 2: ACH Account Information */}
              {currentStep === 2 && (
                <div className="space-y-4 animate-fade-slide-in">
                  <Alert>
                    <AlertDescription>
                      Your ACH account information will be encrypted and stored securely. This
                      information is required for payment processing.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <Label htmlFor="achAccountHolderName">
                      Account Holder Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="achAccountHolderName"
                      placeholder="John Doe"
                      value={formData.achAccountHolderName}
                      onChange={(e) => handleChange('achAccountHolderName', e.target.value)}
                      className={errors.achAccountHolderName ? 'border-destructive' : ''}
                    />
                    {errors.achAccountHolderName && (
                      <p className="text-sm text-destructive">{errors.achAccountHolderName}</p>
                    )}
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="achRoutingNumber">
                        Routing Number <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="achRoutingNumber"
                        placeholder="123456789"
                        maxLength={9}
                        value={formData.achRoutingNumber}
                        onChange={(e) =>
                          handleChange('achRoutingNumber', e.target.value.replace(/\D/g, ''))
                        }
                        className={errors.achRoutingNumber ? 'border-destructive' : ''}
                      />
                      {errors.achRoutingNumber && (
                        <p className="text-sm text-destructive">{errors.achRoutingNumber}</p>
                      )}
                      <p className="text-xs text-muted-foreground">9 digits</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="achAccountNumber">
                        Account Number <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="achAccountNumber"
                        placeholder="1234567890"
                        maxLength={17}
                        value={formData.achAccountNumber}
                        onChange={(e) =>
                          handleChange('achAccountNumber', e.target.value.replace(/\D/g, ''))
                        }
                        className={errors.achAccountNumber ? 'border-destructive' : ''}
                      />
                      {errors.achAccountNumber && (
                        <p className="text-sm text-destructive">{errors.achAccountNumber}</p>
                      )}
                      <p className="text-xs text-muted-foreground">4-17 digits</p>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="achAccountType">
                        Account Type <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={formData.achAccountType}
                        onValueChange={(value: 'checking' | 'savings') =>
                          handleChange('achAccountType', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="checking">Checking</SelectItem>
                          <SelectItem value="savings">Savings</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="achBankName">Bank Name (Optional)</Label>
                      <Input
                        id="achBankName"
                        placeholder="Chase Bank"
                        value={formData.achBankName}
                        onChange={(e) => handleChange('achBankName', e.target.value)}
                      />
                    </div>
                  </div>

                  {errors.general && (
                    <Alert variant="destructive">
                      <AlertDescription>{errors.general}</AlertDescription>
                    </Alert>
                  )}

                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setCurrentStep(1)}
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" size="lg" disabled={isSubmitting}>
                      {isSubmitting ? 'Creating Account...' : 'Create Account'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Address Verification Dialog */}
        <AddressVerificationDialog
          open={showAddressDialog}
          onOpenChange={setShowAddressDialog}
          verificationResult={addressVerificationResult}
          onAcceptSuggested={handleAcceptSuggestedAddress}
          onKeepOriginal={handleKeepOriginalAddress}
        />
      </div>
    </div>
  );
}
